import axios, {
  AxiosInstance,
  AxiosResponse,
  AxiosError,
  InternalAxiosRequestConfig,
} from 'axios';
import { v4 as uuidv4 } from 'uuid';
import {
  LOGISTIC_LOCAL_BASE_URL,
  isLogisticLocalEnabled,
  isLogisticPath,
} from '@/config/logistic-api';
import { storage } from './storage';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

let isLoggingOut = false;

const forceSignOut = () => {
  if (isLoggingOut) return;
  isLoggingOut = true;
  try {
    storage.clear();
    delete Axios.defaults.headers.common['Authorization'];
    if (typeof window !== 'undefined') {
      window.location.href = '/signin';
    }
  } catch (err) {
    console.error('Failed during sign-out:', err);
  } finally {
    isLoggingOut = false;
  }
};

const Axios: AxiosInstance = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
    'dash-client-type': 'web',
  },
});

export const setBearerTokenFromLocalStorage = () => {
  if (typeof window !== 'undefined') {
    const token = storage.getItem('token');
    if (token) {
      Axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      Axios.defaults.headers.common['dash-client-type'] = 'web';
    }
  }
};

Axios.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    config.headers['X-Idempotency-Key'] = uuidv4();
    config.headers['X-Client-Time-Zone'] =
      Intl.DateTimeFormat().resolvedOptions().timeZone;

    // Opt-in localhost routing for `nest-logistic-service` paths only (default
    // off — see `@/config/logistic-api`). Core/fleet paths keep the gateway.
    if (isLogisticLocalEnabled() && isLogisticPath(config.url)) {
      config.baseURL = LOGISTIC_LOCAL_BASE_URL;
    }

    if (typeof window !== 'undefined') {
      const token = storage.getItem('token');
      if (token) {
        config.headers['Authorization'] = `Bearer ${token}`;
      }
    }

    return config;
  },
  (error: AxiosError) => Promise.reject(error)
);

Axios.interceptors.response.use(
  (response: AxiosResponse) => response,
  (error: AxiosError) => {
    if (error.response?.status === 401) {
      if (!error.config?.url?.includes('/v1/auth/user/login')) {
        forceSignOut();
      }
    }
    return Promise.reject(error);
  }
);

setBearerTokenFromLocalStorage();

export default Axios;
